import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { cacheGet, cacheSet } from "@/lib/cache";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  RadarChart, PolarGrid, PolarAngleAxis, Radar, BarChart, Bar, Legend,
} from "recharts";
import { Trophy, Target, TrendingUp, Zap, Award, Activity, Database, Trash2 } from "lucide-react";
import { toast } from "sonner";

const FILTER_KEY = "ct_filters";
const defaultFilters = { entry_type: "", format: "", tournament: "", team: "", practice_type: "", date_from: "", date_to: "" };

function StatCard({ label, value, sub, accent = "#05070A", testid }) {
  return (
    <div className="ct-card p-5" data-testid={testid}>
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</div>
      <div className="stat-num text-4xl mt-1 tabular" style={{ color: accent }}>{value ?? "-"}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

function Skeleton({ h = "h-28" }) { return <div className={`skeleton ${h} w-full`} />; }

export default function Dashboard() {
  const t = useT();
  const [filters, setFilters] = useState(() => { try { return { ...defaultFilters, ...JSON.parse(localStorage.getItem(FILTER_KEY) || "{}") }; } catch { return defaultFilters; } });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tournaments, setTournaments] = useState([]);
  const [teams, setTeams] = useState([]);

  const load = async () => {
    setLoading(true);
    const qs = new URLSearchParams(Object.entries(filters).filter(([, v]) => v)).toString();
    try {
      const { data } = await api.get(`/dashboard?${qs}`);
      setData(data);
      cacheSet(`dashboard?${qs}`, data);
    } catch {
      const cached = cacheGet(`dashboard?${qs}`);
      if (cached) { setData(cached); toast("Showing your last saved stats (offline)"); }
    }
    setLoading(false);
  };

  useEffect(() => { localStorage.setItem(FILTER_KEY, JSON.stringify(filters)); load(); }, [filters]);
  useEffect(() => { api.get("/entries").then(({ data }) => { setTournaments([...new Set(data.map((e) => e.tournament).filter(Boolean))]); setTeams([...new Set(data.map((e) => e.team).filter(Boolean))]); }).catch(() => {}); }, []);

  const loadDemo = async () => { const { data } = await api.post("/demo/load"); toast.success(data.created ? `Loaded ${data.created} demo entries` : data.message); load(); };
  const clearDemo = async () => { const { data } = await api.delete("/demo/clear"); toast.success(`Cleared ${data.deleted} entries`); load(); };

  const set = (k) => (e) => setFilters({ ...filters, [k]: e.target.value });

  const empty = data && data.total_entries === 0;

  const radarData = data ? [
    { m: "Runs/Inn", T: sr(data.comparison.tournament.batting.runs, data.comparison.tournament.batting.innings), P: sr(data.comparison.practice.batting.runs, data.comparison.practice.batting.innings) },
    { m: "Strike Rate", T: data.comparison.tournament.batting.strike_rate || 0, P: data.comparison.practice.batting.strike_rate || 0 },
    { m: "Boundary%", T: data.comparison.tournament.batting.boundary_pct || 0, P: data.comparison.practice.batting.boundary_pct || 0 },
    { m: "Wickets", T: data.comparison.tournament.bowling.wickets || 0, P: data.comparison.practice.bowling.wickets || 0 },
  ] : [];

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h1 className="font-head font-bold text-3xl">{t("Dashboard")}</h1>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={loadDemo} data-testid="load-demo-btn"><Database size={14} /> {t("Load demo")}</Button>
          <Button variant="ghost" size="sm" onClick={clearDemo} data-testid="clear-demo-btn"><Trash2 size={14} /> {t("Clear demo")}</Button>
          <Link to="/app/log"><Button size="sm" className="bg-brand hover:bg-brand-dark" data-testid="quick-log-btn">+ Log</Button></Link>
        </div>
      </div>

      {/* global filter bar */}
      <div className="ct-card p-3 mb-6 flex flex-wrap gap-2 items-center" data-testid="global-filter-bar">
        <Sel value={filters.entry_type} onChange={set("entry_type")} testid="filter-entry-type" opts={[["", t("All types")], ["tournament", t("Tournament")], ["practice", t("Practice")]]} />
        <Sel value={filters.format} onChange={set("format")} testid="filter-format" opts={[["", t("All formats")], ["T20", "T20"], ["ODI", "ODI"], ["T10", "T10"], ["Test", "Test"]]} />
        <Sel value={filters.tournament} onChange={set("tournament")} testid="filter-tournament" opts={[["", t("All tournaments")], ...tournaments.map((t2) => [t2, t2])]} />
        <Sel value={filters.team} onChange={set("team")} testid="filter-team" opts={[["", t("All teams")], ...teams.map((t2) => [t2, t2])]} />
        <input type="date" value={filters.date_from} onChange={set("date_from")} className="h-8 rounded-md border border-slate-200 px-2 text-sm" data-testid="filter-date-from" />
        <input type="date" value={filters.date_to} onChange={set("date_to")} className="h-8 rounded-md border border-slate-200 px-2 text-sm" data-testid="filter-date-to" />
        {Object.values(filters).some(Boolean) && <button onClick={() => setFilters(defaultFilters)} className="text-xs text-brand font-semibold" data-testid="clear-filters">Reset</button>}
      </div>

      {loading ? (
        <div className="grid md:grid-cols-4 gap-4"><Skeleton /><Skeleton /><Skeleton /><Skeleton /></div>
      ) : empty ? (
        <div className="ct-card p-12 text-center" data-testid="empty-state">
          <Activity className="mx-auto text-slate-300 mb-3" size={48} />
          <h3 className="font-head font-bold text-2xl mb-2">{t("No data yet")}</h3>
          <p className="text-slate-500 mb-5">Log your first innings or load demo data to see your analytics come alive.</p>
          <div className="flex gap-3 justify-center">
            <Link to="/app/log"><Button className="bg-brand hover:bg-brand-dark">Log performance</Button></Link>
            <Button variant="outline" onClick={loadDemo}>Load demo data</Button>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* season overview */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label={t("Matches")} value={data.total_entries} sub={`${data.tournament_count} tourn · ${data.practice_count} prac`} testid="stat-matches" />
            <StatCard label={t("Runs")} value={data.batting.runs} sub={`Avg ${data.batting.average ?? "-"}`} accent="#DC2626" testid="stat-runs" />
            <StatCard label={t("Wickets")} value={data.bowling.wickets} sub={`Econ ${data.bowling.economy ?? "-"}`} accent="#1E40AF" testid="stat-wickets" />
            <StatCard label={t("Strike Rate")} value={data.batting.strike_rate ?? "-"} sub={`${data.batting.fifties} fifties · ${data.batting.hundreds} 100s`} accent="#D97706" testid="stat-sr" />
          </div>

          {/* form + consistency + hero */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="ct-card p-5" data-testid="form-index-card">
              <div className="flex items-center gap-2 mb-3"><Zap size={16} className="text-brand" /><span className="text-xs font-bold uppercase tracking-widest text-slate-500">{t("Form & Consistency")}</span></div>
              <div className="grid grid-cols-2 gap-4">
                <Gauge label={t("Form Index")} value={data.form_index} color="#D90416" />
                <Gauge label={t("Consistency")} value={data.consistency_score} color="#1E40AF" note={data.consistency_score === null ? "Needs 5+ innings" : null} />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4">
              <HeroCard title={t("Best Batting")} icon={Trophy} color="#DC2626" data={data.best_batting}
                render={(d) => `${d.runs}${d.balls ? ` (${d.balls})` : ""}`} sub={(d) => `${d.opponent || ""} · ${d.date || ""}`} testid="best-batting-hero-card" />
              <HeroCard title={t("Best Bowling")} icon={Award} color="#1E40AF" data={data.best_bowling}
                render={(d) => `${d.wickets}/${d.runs}`} sub={(d) => `${d.opponent || ""} · ${d.date || ""}`} testid="best-bowling-hero-card" />
            </div>
          </div>

          {/* trends */}
          <div className="grid md:grid-cols-2 gap-4">
            <ChartCard title={t("Batting Trend")} testid="batting-trend-chart" summary={`Runs across ${data.batting_trend.length} innings`}>
              {data.batting_trend.length ? (
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={data.batting_trend}><CartesianGrid strokeDasharray="3 3" stroke="#eee" /><XAxis dataKey="date" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} /><Tooltip /><Line type="monotone" dataKey="runs" stroke="#DC2626" strokeWidth={2} dot={{ r: 3 }} /></LineChart>
                </ResponsiveContainer>
              ) : <Empty />}
            </ChartCard>
            <ChartCard title={t("Bowling Trend")} testid="bowling-trend-chart" summary={`Wickets across ${data.bowling_trend.length} spells`}>
              {data.bowling_trend.length ? (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={data.bowling_trend}><CartesianGrid strokeDasharray="3 3" stroke="#eee" /><XAxis dataKey="date" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} /><Tooltip /><Bar dataKey="wickets" fill="#1E40AF" radius={[4, 4, 0, 0]} /></BarChart>
                </ResponsiveContainer>
              ) : <Empty />}
            </ChartCard>
          </div>

          {/* radar comparison */}
          <ChartCard title={t("Practice vs Tournament")} testid="radar-comparison-chart" summary="Compares your tournament output against practice sessions across key metrics.">
            <div className="grid md:grid-cols-2 gap-4 items-center">
              <ResponsiveContainer width="100%" height={260}>
                <RadarChart data={radarData}><PolarGrid /><PolarAngleAxis dataKey="m" tick={{ fontSize: 11 }} /><Radar name="Tournament" dataKey="T" stroke="#D90416" fill="#D90416" fillOpacity={0.35} /><Radar name="Practice" dataKey="P" stroke="#1E293B" fill="#1E293B" fillOpacity={0.25} /><Legend /></RadarChart>
              </ResponsiveContainer>
              <p className="text-sm text-slate-600 leading-relaxed">
                In tournaments you average <b>{sr(data.comparison.tournament.batting.runs, data.comparison.tournament.batting.innings)}</b> runs/innings at a strike rate of <b>{data.comparison.tournament.batting.strike_rate ?? "-"}</b>.
                {" "}In practice your strike rate is <b>{data.comparison.practice.batting.strike_rate ?? "-"}</b>. {compareLine(data)}
              </p>
            </div>
          </ChartCard>
        </div>
      )}
    </div>
  );
}

function sr(runs, inns) { return inns ? Math.round(runs / inns) : 0; }
function compareLine(d) {
  const t = d.comparison.tournament.batting.strike_rate || 0, p = d.comparison.practice.batting.strike_rate || 0;
  if (!t && !p) return "";
  if (t > p) return "You go harder under match pressure — nice sign of a big-game player.";
  return "Your nets intent is higher than match day — try carrying that freedom into games.";
}

function Sel({ value, onChange, opts, testid }) {
  return <select value={value} onChange={onChange} data-testid={testid} className="h-8 rounded-md border border-slate-200 px-2 text-sm bg-white">{opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>;
}
function Gauge({ label, value, color, note }) {
  const pct = value ?? 0;
  return (
    <div className="text-center">
      <div className="relative w-20 h-20 mx-auto">
        <svg viewBox="0 0 36 36" className="w-20 h-20 -rotate-90">
          <circle cx="18" cy="18" r="15.9" fill="none" stroke="#e2e8f0" strokeWidth="3" />
          <circle cx="18" cy="18" r="15.9" fill="none" stroke={color} strokeWidth="3" strokeDasharray={`${pct} 100`} strokeLinecap="round" />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center stat-num text-xl">{value ?? "-"}</div>
      </div>
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mt-1">{label}</div>
      {note && <div className="text-[9px] text-slate-400">{note}</div>}
    </div>
  );
}
function HeroCard({ title, icon: Icon, color, data, render, sub, testid }) {
  return (
    <div className="rounded-xl p-5 text-white grain relative overflow-hidden" style={{ background: color }} data-testid={testid}>
      <div className="flex items-center gap-2 text-white/80 text-xs font-bold uppercase tracking-widest"><Icon size={14} />{title}</div>
      {data ? (<><div className="stat-num text-4xl mt-2 tabular">{render(data)}</div><div className="text-xs text-white/80 mt-1">{sub(data)}</div></>) : <div className="text-white/70 mt-2 text-sm">No entries yet</div>}
    </div>
  );
}
function ChartCard({ title, children, summary, testid }) {
  return (
    <div className="ct-card p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3"><TrendingUp size={16} className="text-slate-400" /><span className="text-xs font-bold uppercase tracking-widest text-slate-500">{title}</span></div>
      {summary && <p className="sr-only">{summary}</p>}
      {children}
    </div>
  );
}
function Empty() { return <div className="h-[220px] flex items-center justify-center text-slate-400 text-sm">Not enough data</div>; }
